import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DEFAULT_LIVE_WINDOW_MINUTES, PRODUCTION_STOPS_ENDPOINT, PRODUCTION_STOPS_REFRESH_INTERVAL_MS } from './machineStatDataConstants';

const EMPTY_STOPS = [];

export default function useProductionStops({ dataset, datasets, dataByArea, rangeMode, from, to }) {
  const areas = useMemo(() => datasets.filter((item) => item.allowStopAnnotations).map((item) => item.key), [datasets]);
  const enabled = areas.length > 0;
  const area = dataset?.key;
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState({ entries: EMPTY_STOPS, loading: false, error: '' });
  const fallback = useMemo(
    () => ({
      from: rangeMode === 'live' ? new Date(Date.now() - DEFAULT_LIVE_WINDOW_MINUTES * 60_000).toISOString() : from,
      to: rangeMode === 'live' ? new Date().toISOString() : to
    }),
    [area, from, rangeMode, to]
  );
  const points = dataByArea[area] ?? EMPTY_STOPS;
  const last = points[points.length - 1];
  const start = points[0]?.date ?? fallback.from;
  const end = last ? new Date(Date.parse(last.date) + (last.durationMinutes ?? 1) * 60_000).toISOString() : fallback.to;
  // Net running time covers whole days, even when the graph shows a smaller range.
  const firstDay = new Date(start);
  firstDay.setHours(0, 0, 0, 0);
  const nextDay = new Date(Date.parse(end) - 1);
  nextDay.setHours(24, 0, 0, 0);
  const queryStart = firstDay.getTime();
  const queryEnd = nextDay.getTime();
  const scope = `${areas.join('|')}|${queryStart}|${queryEnd}`;

  useEffect(() => {
    if (!enabled || !Number.isFinite(queryStart) || !Number.isFinite(queryEnd)) return undefined;
    let cancelled = false;
    let inFlight = false;
    const fetchStops = async (background = false) => {
      if (inFlight) return;
      inFlight = true;
      if (!background) setData((current) => ({ ...current, scope, entries: current.scope === scope ? current.entries : EMPTY_STOPS, loading: true, error: '' }));
      try {
        const response = await axios.get(PRODUCTION_STOPS_ENDPOINT, { params: { from: new Date(queryStart).toISOString(), to: new Date(queryEnd).toISOString() } });
        if (!cancelled) {
          setData((current) => {
            const unchanged = current.scope === scope && JSON.stringify(current.entries) === JSON.stringify(response.data.results);
            if (unchanged && current.timeZone === response.data.time_zone && !current.loading && !current.error) return current;
            return { scope, entries: unchanged ? current.entries : response.data.results, timeZone: response.data.time_zone, loading: false, error: '' };
          });
        }
      } catch (error) {
        if (!cancelled) setData((current) => ({ ...current, loading: false, error: error.response?.data?.detail ?? 'Could not load recorded stops.' }));
      } finally {
        inFlight = false;
      }
    };
    fetchStops();
    const timer = window.setInterval(() => fetchStops(true), PRODUCTION_STOPS_REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [enabled, queryEnd, queryStart, revision, scope]);

  const entries = enabled && data.scope === scope ? data.entries : EMPTY_STOPS;
  const allStops = useMemo(
    () => entries.filter((stop) => areas.includes(stop.area_identifier)).map((stop) => ({ ...stop, start: Date.parse(stop.started_at), end: Date.parse(stop.ended_at) })),
    [areas, entries]
  );
  const stops = useMemo(() => allStops.filter((stop) => stop.area_identifier === area && stop.start < Date.parse(end) && stop.end > Date.parse(start)), [allStops, area, end, start]);
  return {
    stops,
    allStops,
    timeZone: dataset?.allowStopAnnotations ? data.timeZone : undefined,
    loading: enabled && (data.scope !== scope || data.loading),
    error: enabled && data.scope === scope ? data.error : '',
    refresh: () => setRevision((current) => current + 1),
    save: (id, payload) => (id ? axios.patch(`${PRODUCTION_STOPS_ENDPOINT}${id}/`, payload) : axios.post(PRODUCTION_STOPS_ENDPOINT, payload)),
    remove: (id) => axios.delete(`${PRODUCTION_STOPS_ENDPOINT}${id}/`)
  };
}
